import React from 'react';
import { ArrowRight } from 'lucide-react';
import Layout from './Layout';
import FadeIn from './FadeIn';

interface CaseStudy {
  company: string;
  logo: string;
  industry: string;
  challenge: string;
  solution: string;
  results: {
    metric: string;
    value: string;
  }[];
  quote: {
    text: string;
    author: string;
    role: string;
  };
}

const caseStudies: CaseStudy[] = [
  {
    company: "TechVision",
    logo: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&h=200&q=80",
    industry: "Tech",
    challenge: "Difficulté à attirer des talents tech dans un marché compétitif",
    solution: "Utilisation de l'IA pour créer des offres d'emploi personnalisées",
    results: [
      { metric: "Temps de recrutement", value: "-40%" },
      { metric: "Candidatures qualifiées", value: "+75%" },
      { metric: "Taux de conversion", value: "+50%" }
    ],
    quote: {
      text: "PimpMyJob nous a permis d'optimiser drastiquement notre processus de recrutement tech.",
      author: "Marie Dubois",
      role: "DRH @ TechVision"
    }
  }
];

const CaseStudiesSection: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <Layout>
        <FadeIn>
          <div className="text-center mb-16">
            <h2 className="text-h2-mobile md:text-h2 mb-4">
              Ils ont transformé leur recrutement avec <span className="gradient-text">PimpMyJob</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Découvrez comment des entreprises innovantes utilisent notre IA pour optimiser leur processus de recrutement.
            </p>
          </div>
        </FadeIn>

        {caseStudies.map((study, index) => (
          <FadeIn key={index} delay={100}>
            <div className="bg-gray-50 rounded-2xl p-8 md:p-12 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <div className="flex items-center mb-6">
                    <img 
                      src={study.logo} 
                      alt={study.company} 
                      className="w-16 h-16 rounded-lg mr-4"
                    />
                    <div>
                      <h3 className="text-xl font-semibold">{study.company}</h3>
                      <p className="text-gray-600">{study.industry}</p>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h4 className="text-lg font-semibold mb-2">Le Challenge</h4>
                    <p className="text-gray-600">{study.challenge}</p>
                  </div>

                  <div className="mb-8">
                    <h4 className="text-lg font-semibold mb-2">Notre Solution</h4>
                    <p className="text-gray-600">{study.solution}</p>
                  </div>

                  <blockquote className="border-l-4 border-indigo-500 pl-4 italic text-gray-700">
                    "{study.quote.text}"
                    <footer className="mt-2 text-sm text-gray-600 not-italic">
                      {study.quote.author} - {study.quote.role}
                    </footer>
                  </blockquote>
                </div>

                <div>
                  <div className="bg-white rounded-xl p-8 shadow-lg">
                    <h4 className="text-lg font-semibold mb-6">Résultats</h4>
                    <div className="space-y-6">
                      {study.results.map((result, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-gray-600">{result.metric}</span>
                          <span className="text-2xl font-bold text-indigo-600">{result.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-8 text-center">
                    <button className="btn-primary inline-flex items-center">
                      <span>Voir l'étude complète</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        ))}
      </Layout>
    </section>
  );
};

export default CaseStudiesSection;